import React, { useState, useEffect } from 'react';
import { Asset } from '@/firebase/assetService';
import { Location } from '@/firebase/locationService';
import { floorService, Floor } from '@/firebase/floorService';
import { assetService } from '@/firebase/assetService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { X, MapPin } from 'lucide-react';

const lastFloorStorageKey = (orgId: string, locationId: string) =>
  `assetFloorTag:lastFloor:${orgId}:${locationId}`;

function readLastFloorId(orgId: string, locationId: string): string | null {
  try {
    return localStorage.getItem(lastFloorStorageKey(orgId, locationId));
  } catch {
    return null;
  }
}

function writeLastFloorId(orgId: string, locationId: string, floorId: string) {
  try {
    localStorage.setItem(lastFloorStorageKey(orgId, locationId), floorId);
  } catch {
    /* ignore quota / private mode */
  }
}

interface AssetFloorTagModalProps {
  asset: Asset;
  location: Location;
  onClose: () => void;
  onSaved: () => void;
}

const AssetFloorTagModal: React.FC<AssetFloorTagModalProps> = ({
  asset,
  location,
  onClose,
  onSaved,
}) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [floors, setFloors] = useState<Floor[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<string>('');
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    asset.floorId === undefined ? null : asset.floorMapPosition || null
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!location?.id || !currentOrganization?.id) return;
    floorService.getFloorsByLocation(location.id, currentOrganization.id).then((r) => {
      if (r.success && r.data) {
        setFloors(r.data);
        if (asset.floorId && r.data.some((f) => f.id === asset.floorId)) {
          setSelectedFloorId(asset.floorId);
        } else if (r.data.length > 0) {
          const last =
            location.id && currentOrganization.id
              ? readLastFloorId(currentOrganization.id, location.id)
              : null;
          const lastValid = last && r.data.some((f) => f.id === last) ? last : null;
          setSelectedFloorId(lastValid || r.data[0].id || '');
        }
      }
    });
  }, [location?.id, currentOrganization?.id, asset.floorId]);

  useEffect(() => {
    setSelectedFloor(floors.find((f) => f.id === selectedFloorId) || null);
    if (asset.floorId === selectedFloorId && asset.floorMapPosition) {
      setPosition(asset.floorMapPosition);
    } else {
      setPosition(null);
    }
  }, [floors, selectedFloorId, asset.floorId, asset.floorMapPosition]);

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedFloor?.floorMapImageUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setPosition({ x, y });
  };

  const handleSave = async () => {
    if (!asset.id || !selectedFloorId || !position || !currentOrganization?.id) {
      showNotification('Select a floor and click on the map to set position', 'error');
      return;
    }
    setIsSaving(true);
    const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
    const result = await assetService.updateAsset(
      asset.id,
      { floorId: selectedFloorId, floorMapPosition: position },
      userInfo,
      currentOrganization.id
    );
    setIsSaving(false);
    if (result.success) {
      if (location.id) {
        writeLastFloorId(currentOrganization.id, location.id, selectedFloorId);
      }
      showNotification('Asset tagged to floor map', 'success');
      onSaved();
      onClose();
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleClear = async () => {
    if (!asset.id || !currentOrganization?.id) return;
    setIsSaving(true);
    const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
    const result = await assetService.removeFloorTag(asset.id, userInfo, currentOrganization.id);
    setIsSaving(false);
    if (result.success) {
      setPosition(null);
      showNotification('Floor tag removed', 'success');
      onSaved();
      onClose();
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[150] flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary-600" />
            Tag {asset.assetName} on Floor Map
          </h2>
          {asset.floorId && selectedFloor && (
            <span className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
              Already tagged to {selectedFloor.name}
            </span>
          )}
          {floors.length > 0 && (
            <select
              value={selectedFloorId}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedFloorId(id);
                if (location.id && currentOrganization?.id && id) {
                  writeLastFloorId(currentOrganization.id, location.id, id);
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded text-sm"
            >
              {floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {!f.floorMapImageUrl && ' (no map)'}
                </option>
              ))}
            </select>
          )}
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 flex flex-col min-h-0 p-4">
        {floors.length === 0 ? (
          <div className="flex items-center justify-center flex-1">
            <p className="text-gray-500">
              No floors with floor plans for {location.name}. Add floors in Locations → Manage Floors.
            </p>
          </div>
        ) : !selectedFloor?.floorMapImageUrl ? (
          <div className="flex items-center justify-center flex-1">
            <p className="text-gray-500">Upload a floor plan for this floor in Locations → Manage Floors.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <p className="text-sm text-gray-600 mb-2 shrink-0">Click on the map to set the asset position.</p>
            <div
              className="flex-1 min-h-0 relative rounded-lg border border-gray-200 overflow-hidden bg-gray-100 cursor-crosshair"
              onClick={handleMapClick}
            >
              <img
                src={selectedFloor.floorMapImageUrl}
                alt={selectedFloor.name}
                className="absolute inset-0 w-full h-full object-contain"
              />
              {position && (
                <div
                  className="absolute w-8 h-8 -ml-4 -mt-4 rounded-full bg-red-500 border-2 border-white shadow-lg pointer-events-none"
                  style={{
                    left: `${position.x * 100}%`,
                    top: `${position.y * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t bg-white flex justify-between shrink-0">
        <button
          onClick={handleClear}
          className="px-4 py-2 text-sm text-gray-600 hover:text-red-600"
          disabled={!asset.floorId}
        >
          Remove tag
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!position || isSaving}
            className="px-4 py-2 bg-primary-600 text-white rounded text-sm disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetFloorTagModal;
